/**
 * One food, created or corrected.
 *
 * The by-hand form and the product correction were always the same form: a
 * name, a brand, four figures per 100 and a portion. What differed was where
 * you could reach each one from — the first only after a search that found
 * nothing, the second only through a logged entry — and so they were built
 * twice and drifted. Here they are one screen, and which of the two it is
 * doing is decided by whether there is a food at the end of the URL.
 *
 * **The scope question is the point of it.** `food_items` is shared, so this is
 * a data-integrity question wearing a form's clothes. The backend has always
 * split by source — a barcode row is objective, so a fix to it is a fix for
 * everyone and is audited, while a private guess forks — and the UI never
 * mentioned it. The rule was applied silently and correctly, which is the worst
 * arrangement available: somebody who meant "only my copy" and got "everyone's"
 * had no way to discover it until a stranger disagreed with their figures.
 *
 * So the choice is explicit, in plain words, **between the numbers and Save**,
 * where it cannot be skipped past. And it appears only once a value has
 * actually moved: asked before anything has changed it is a setting nobody
 * reads, and asked after the button it is a confirmation dialogue people click
 * through.
 *
 * Entries already logged keep the macros they were logged with, either way —
 * `food_logs` snapshots them at write time, precisely so that correcting a
 * product cannot rewrite somebody's finished week.
 *
 * The portion is part of the correction, and travels the same road. It was
 * read-only for a while because nothing could save it, which left the one
 * number people most want to change about a food they eat every day — how much
 * of it — as the one number the form refused. It is objective in the same way
 * the figures are: a barcode row's serving size is printed on the packet beside
 * the nutrition, so fixing it is a fix for everyone, and "I eat half of one" is
 * a private copy. The scope question covers both, and names whichever moved.
 */

import {
  figureChange,
  figureMoved,
  figureValue,
  NUTRIENTS,
  parseDecimal,
  seedFigure,
  useCorrectFood,
  useCreateFood,
  useFavourites,
  useFood,
  useSetFavourite,
  type CorrectionScope,
  type FigureSpec,
  type FoodEdit,
  type FoodItem,
} from "@calorio/core";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useCalorio } from "../app/providers";
import { OptionalDetail } from "../components/OptionalDetail";

/** Which sources carry a truth printed on a packet that anyone can check. */
const OBJECTIVE = new Set(["off", "label_ocr"]);

interface Field extends FigureSpec {
  key: keyof FoodItem & string;
  label: string;
  /** Shown after the label where it is not the basis unit — salt is in grams. */
  unit?: string;
}

/** The four every food has, and cannot be logged without. */
const CORE: readonly Field[] = [
  { key: "kcal_per_100", label: "Calories", required: true },
  { key: "protein_g_per_100", label: "Protein", required: true },
  { key: "carbs_g_per_100", label: "Carbs", required: true },
  { key: "fat_g_per_100", label: "Fat", required: true },
];

/**
 * The four a packet may or may not print.
 *
 * Not `required`, so a blank is a deliberate clear rather than a mistake, and
 * `figureChange` sends `null` for it. Salt is stored as sodium in milligrams
 * and typed in grams — the spec carries the conversion in both directions, so
 * the comparison happens in the unit that was typed.
 */
const DETAIL: readonly Field[] = NUTRIENTS.map((spec) => ({
  key: spec.per100Key,
  label: spec.label,
  unit: spec.unit,
  decimals: spec.decimals,
  display: spec.display,
  store: spec.store,
}));

const FIELDS: readonly Field[] = [...CORE, ...DETAIL];

export function FoodEditor() {
  const { id } = useParams();
  const ctx = useCalorio();
  const existing = useFood(ctx, id ?? null);

  if (id && existing.isLoading) return <p className="muted">Loading…</p>;
  if (id && !existing.data) return <p className="error">No such food.</p>;

  // Keyed on the loaded food so the fields' initial state comes from real data
  // rather than from a first render that had none.
  return <Editor key={existing.data?.id ?? "new"} food={existing.data ?? null} />;
}

function Editor({ food }: { food: FoodItem | null }) {
  const ctx = useCalorio();
  const navigate = useNavigate();
  const create = useCreateFood(ctx);
  const correct = useCorrectFood(ctx);
  const favourites = useFavourites(ctx);
  const setFavourite = useSetFavourite(ctx);

  const [name, setName] = useState(food?.name ?? "");
  const [brand, setBrand] = useState(food?.brand ?? "");
  const [unit, setUnit] = useState<"g" | "ml">(food?.basis_unit ?? "g");
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      FIELDS.map((field) => [
        field.key,
        food ? seedFigure(food[field.key] as number | null, field) : "",
      ]),
    ),
  );
  const saved = food?.servings.find((s) => s.is_default)?.grams ?? null;
  // Seeded at the precision it is compared at, like the figures above: a
  // portion of 12.5 shown as 13 would file a correction the moment anything
  // else on the form was saved.
  const [portion, setPortion] = useState(saved === null ? "" : String(round(saved)));
  const [problem, setProblem] = useState<string | null>(null);

  /**
   * Whether the shared row is a thing this user is allowed to change for
   * everybody. Offering "fix the product" on a food nobody else can see would
   * be a choice with one real option, and the server refuses it anyway.
   */
  const shareable = food !== null && OBJECTIVE.has(food.source) && food.shared;
  const [scope, setScope] = useState<CorrectionScope>(shareable ? "product" : "mine");

  // Three-way, not two — see the note in `figures.ts`. `round(null)` is 0, so
  // the old comparison read an unset figure as "was nought, now blank" and
  // claimed the user had cleared something that was never there.
  const changesByKey = new Map(
    FIELDS.map((field) => [
      field.key,
      figureChange(values[field.key], (food?.[field.key] ?? null) as number | null, field),
    ]),
  );
  const edited = food ? FIELDS.filter((field) => figureMoved(changesByKey.get(field.key)!)) : [];

  /** The portion as typed: a number, or `null` for "this food hasn't got one". */
  const typedPortion = portion.trim() === "" ? null : parseDecimal(portion);
  const portionMoved =
    food !== null &&
    (typedPortion === null
      ? saved !== null
      : saved === null || round(typedPortion) !== round(saved));

  /** Everything that moved, in the words the scope question asks about. */
  const moved = [
    ...edited.map((field) => field.label.toLowerCase()),
    ...(portionMoved ? ["portion"] : []),
  ];

  const starred = food !== null && !!favourites.data?.some((f) => f.id === food.id);

  const box = (field: Field) => {
    const changed = edited.some((e) => e.key === field.key);
    return (
      <label className="manual__row" key={field.key}>
        <span>
          {field.label}
          {field.unit && <span className="muted"> ({field.unit})</span>}
        </span>
        <input
          className={`field num manual__value${changed ? " is-edited" : ""}`}
          inputMode="decimal"
          value={values[field.key] ?? ""}
          onChange={(event) =>
            setValues((current) => ({ ...current, [field.key]: event.target.value }))
          }
          placeholder="—"
          aria-label={
            field.unit
              ? `${field.label} ${field.unit} per 100 ${unit}`
              : `${field.label} per 100 ${unit}`
          }
        />
      </label>
    );
  };

  const save = async () => {
    setProblem(null);
    if (!name.trim()) return setProblem("Give it a name.");

    const figures = Object.fromEntries(
      FIELDS.map((field) => [field.key, parseDecimal(values[field.key] ?? "")]),
    );
    if (!Number.isFinite(figures.kcal_per_100) || (figures.kcal_per_100 as number) < 0) {
      return setProblem("Calories per 100 must be a number.");
    }
    if (FIELDS.some((field) => changesByKey.get(field.key)!.state === "invalid")) {
      return setProblem("Every figure has to be a number, and none of them can be negative.");
    }

    try {
      if (food) {
        // Only what moved. Sending every field back would mark an untouched
        // food as corrected, and the audit trail would say somebody reviewed
        // numbers they never looked at.
        const changes: FoodEdit = Object.fromEntries(
          edited.map((field) => [field.key, figureValue(changesByKey.get(field.key)!)]),
        );
        if (name.trim() !== food.name) changes.name = name.trim();
        if (brand.trim() !== (food.brand ?? "")) changes.brand = brand.trim();
        if (portionMoved) {
          if (typedPortion !== null && (!Number.isFinite(typedPortion) || typedPortion <= 0)) {
            return setProblem(
              "A portion has to be more than nothing. Leave it empty if there isn't one.",
            );
          }
          // `null` is a value here, not an omission: it says this food has no
          // useful portion, and the server takes the serving away.
          changes.portion_g = typedPortion;
        }
        if (Object.keys(changes).length) {
          await correct.mutateAsync({ id: food.id, changes, scope });
        }
      } else {
        // Macros default to zero rather than blocking: someone entering a drink
        // knows the calories and may genuinely not know the rest, and refusing
        // the whole food over a blank fat field helps nobody.
        const grams = parseDecimal(portion);
        await create.mutateAsync({
          name: name.trim(),
          brand: brand.trim() || null,
          basis_unit: unit,
          kcal_per_100: figures.kcal_per_100 as number,
          protein_g_per_100: (figures.protein_g_per_100 as number) || 0,
          carbs_g_per_100: (figures.carbs_g_per_100 as number) || 0,
          fat_g_per_100: (figures.fat_g_per_100 as number) || 0,
          // `|| 0` above and not here, deliberately. The macros are NOT NULL
          // columns and a blank has to become something; these are nullable,
          // and a blank means the packet did not say. Filling it with nought
          // would assert that a food has no sugar in it.
          ...Object.fromEntries(
            DETAIL.map((field) => [field.key, figureValue(changesByKey.get(field.key)!)]),
          ),
          servings:
            Number.isFinite(grams) && grams > 0
              ? [{ label: "1 portion", grams, is_default: true }]
              : [],
        });
      }
      navigate("/foods");
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "Couldn't save that.");
    }
  };

  const saving = create.isPending || correct.isPending;

  return (
    <div className="editor">
      <div className="editor__back">
        <button className="editor__back-link" onClick={() => navigate("/foods")}>
          ‹ Foods
        </button>
        {food && (
          <button
            className="entry__star"
            aria-pressed={starred}
            aria-label={starred ? `Unstar ${food.name}` : `Star ${food.name}`}
            onClick={() => setFavourite.mutate({ foodId: food.id, favourite: !starred })}
          >
            {starred ? "★" : "☆"}
          </button>
        )}
      </div>

      <h1 className="editor__title">{food ? food.name : "New food"}</h1>
      {/* Where this row came from, and whether anyone else can see it. Both
          decide what the scope question below is even allowed to offer: a
          barcode row is one other people scan, a private one is only ever
          yours. */}
      {food && (
        <p className="num editor__meta">
          {food.barcode ? `Barcode ${food.barcode}` : sourceWord(food.source)}
          {food.shared ? " · shared" : " · private to you"}
        </p>
      )}

      <input
        className={`field${food && name !== food.name ? " is-edited" : ""}`}
        value={name}
        onChange={(event) => setName(event.target.value)}
        placeholder="Name"
        aria-label="Name"
      />
      <input
        className={`field${food && brand !== (food.brand ?? "") ? " is-edited" : ""}`}
        value={brand}
        onChange={(event) => setBrand(event.target.value)}
        placeholder="Brand (optional)"
        aria-label="Brand"
      />

      <div className="editor__unit">
        <span className="muted">Nutrition per 100</span>
        {(["g", "ml"] as const).map((option) => (
          <button
            key={option}
            className="editor__unit-pill num"
            aria-pressed={unit === option}
            // A food's basis unit is a fact about the packet, and changing it
            // on an existing row would silently rescale every future portion.
            disabled={food !== null}
            onClick={() => setUnit(option)}
          >
            {option}
          </button>
        ))}
      </div>

      {CORE.map(box)}

      {/* Open when the food already carries one of them. Hiding a figure that
          exists is worse than the extra height; hiding four empty boxes is the
          point. A new food starts shut — there is nothing to hide yet. */}
      <OptionalDetail defaultOpen={DETAIL.some((f) => (food?.[f.key] ?? null) !== null)}>
        {DETAIL.map(box)}
      </OptionalDetail>

      <label className="manual__row">
        <span>
          Usual portion<span className="muted"> ({unit})</span>
        </span>
        <input
          className={`field num manual__value${portionMoved ? " is-edited" : ""}`}
          inputMode="decimal"
          value={portion}
          onChange={(event) => setPortion(event.target.value)}
          placeholder="—"
          aria-label={`Usual portion in ${unit}`}
        />
      </label>
      {food && (
        <p className="manual__note">
          This is the weight the add sheet offers before anything is weighed. Emptying it says
          there isn't a useful one. Entries you have already logged keep the weight they were
          logged at.
        </p>
      )}

      {/* Shown only once a value has actually moved, and placed where it cannot
          be skipped past. Above the fields it is a setting; after the button it
          is a dialogue. */}
      {shareable && moved.length > 0 && (
        <div className="correct__scope">
          <span className="correct__scope-label">{moved.join(", ")} changed — who gets it?</span>

          <button
            className={`correct__option${scope === "product" ? " is-on" : ""}`}
            aria-pressed={scope === "product"}
            onClick={() => setScope("product")}
          >
            <span className="correct__radio" aria-hidden="true" />
            <span>
              <span className="correct__option-title">Fix the product</span>
              <span className="correct__option-sub">
                The packet says otherwise — its figures, or its serving size. Everyone who scans
                this barcode gets the correction, and it is audited.
              </span>
            </span>
          </button>

          <button
            className={`correct__option${scope === "mine" ? " is-on" : ""}`}
            aria-pressed={scope === "mine"}
            onClick={() => setScope("mine")}
          >
            <span className="correct__radio" aria-hidden="true" />
            <span>
              <span className="correct__option-title">Keep a private copy</span>
              <span className="correct__option-sub">
                Mine is different from the packet, or I eat a different amount of it. Forks a copy
                only you see.
              </span>
            </span>
          </button>

          <p className="manual__note">
            Entries already logged keep the macros they were logged with, either way.
          </p>
        </div>
      )}

      {problem && (
        <p className="error" style={{ margin: 0 }}>
          {problem}
        </p>
      )}

      <div className="editor__actions">
        <button className="button editor__save" onClick={() => void save()} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </button>
        <button className="editor__cancel" onClick={() => navigate("/foods")}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/** One decimal place, the precision the fields are seeded at — so re-saving an
 *  untouched field cannot file a correction for a rounding difference. */
const round = (n: number) => Math.round(n * 10) / 10;

function sourceWord(source: string): string {
  if (source === "label_ocr") return "Read off a label";
  if (source === "generic") return "Reference data";
  if (source === "off") return "From a barcode";
  return "Entered by hand";
}
